import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';

const BarrelSelectionModal = ({ 
  show, 
  wineID,
  wineContainers,
  selectedBarrels = [], 
  onClose, 
  onSave 
}) => {
  const [selected, setSelected] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (show) {
      setSelected(selectedBarrels || []);
    } else {
      setSearchTerm('');
    }
  }, [selectedBarrels, show]);

  const handleSave = () => {
    onSave(selected);
  };

  // Filter containers based on search term
  const filteredContainers = wineID && wineContainers[wineID] 
    ? wineContainers[wineID].filter(container => {
        return (
          container.cntDesc.toLowerCase().includes(searchTerm.toLowerCase()) ||
          container.containerID.toString().includes(searchTerm)
        );
      })
    : [];

  // Group containers by type (e.g., Barrel, Tank)
  const groupedContainers = filteredContainers.reduce((acc, container) => {
    // Extract container type from description (assuming format like "Barrel 123" or "Tank 5")
    const type = container.cntDesc.split(' ')[0] || 'Other';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(container);
    return acc;
  }, {});

  if (!show) return null;

  return (
    <>
      <div className="modal-backdrop fade show"></div>
      <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Select Barrels</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <div className="input-group">
                  <span className="input-group-text">
                    <Search size={16} />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search barrels..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {filteredContainers.length > 0 ? (
                <div className="accordion" id="containerAccordion">
                  {Object.entries(groupedContainers).map(([type, containers], typeIndex) => (
                    <div className="accordion-item" key={type}>
                      <h2 className="accordion-header" id={`heading-${type}`}>
                        <button 
                          className="accordion-button" 
                          type="button" 
                          data-bs-toggle="collapse" 
                          data-bs-target={`#collapse-${type}`} 
                          aria-expanded={typeIndex === 0 ? "true" : "false"} 
                          aria-controls={`collapse-${type}`}
                        >
                          {type} ({containers.length})
                        </button>
                      </h2>
                      <div 
                        id={`collapse-${type}`} 
                        className={`accordion-collapse collapse ${typeIndex === 0 ? 'show' : ''}`} 
                        aria-labelledby={`heading-${type}`}
                      >
                        <div className="accordion-body">
                          <div className="row">
                            {containers.map(container => {
                              const isSelected = selected.includes(container.containerID);
                              
                              return (
                                <div key={container.containerID} className="col-md-4 mb-2">
                                  <div 
                                    className={`card cursor-pointer ${isSelected ? 'bg-primary text-white' : ''}`}
                                    onClick={() => {
                                      const newSelected = isSelected
                                        ? selected.filter(id => id !== container.containerID)
                                        : [...selected, container.containerID];
                                      
                                      setSelected(newSelected);
                                    }}
                                  >
                                    <div className="card-body p-2">
                                      <div className="form-check">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          id={`barrel-${container.containerID}`}
                                          checked={isSelected}
                                          onChange={() => {}}
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                        <label className="form-check-label" htmlFor={`barrel-${container.containerID}`}>
                                          <strong>#{container.containerID} - {container.cntDesc}</strong>
                                          <br />
                                          <small>{container.cntGallons} gallons</small>
                                        </label>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="alert alert-info">
                  {searchTerm 
                    ? "No barrels match your search" 
                    : wineID 
                      ? "No barrels available for this wine lot" 
                      : "Please select a wine lot first"}
                </div>
              )}

              {selected.length > 0 && (
                <div className="mt-3 alert alert-success">
                  <strong>{selected.length} barrels selected</strong>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <div className="d-flex justify-content-between w-100">
                <div>
                  {selected.length > 0 && (
                    <button 
                      type="button" 
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => setSelected([])}
                    >
                      Clear Selection
                    </button>
                  )}
                </div>
                <div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary me-2"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={handleSave}
                  >
                    Use Selected Barrels
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .cursor-pointer {
          cursor: pointer;
        }
        .card:hover:not(.bg-primary) {
          background-color: rgba(0,0,0,0.03);
        }
      `}</style>
    </>
  );
};

export default BarrelSelectionModal;